import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, Form, FormikProvider, FieldArray } from "formik";
import { Select, Input } from "antd";
import {
  createMentor,
  updateMentor,
  getMentorList,
} from "../../appRedux/actions/mentors";
import { hideDialog } from "../../appRedux/actions/common";
import UploadImage from "../Common/UploadImage";

const { Option } = Select;

const INITIAL_CREDENTIAL = {
  type: "Certification",
  title: "",
  institution: "",
  year: "",
};

const INITIAL_STATE = {
  name: "",
  displayName: "",
  email: "",
  phone: "",
  profileImage: "",
  headline: "",
  bio: "",
  designation: "",
  organization: "",
  yearsOfExperience: 0,
  domains: [],
  expertiseAreas: [],
  credentials: [],
  linkedinUrl: "",
  websiteUrl: "",
  status: "DRAFT",
};

const CREDENTIAL_TYPE_OPTIONS = [
  "Degree",
  "Certification",
  "Professional",
  "Award",
];

const PREDEFINED_DOMAINS = [
  "Data Science",
  "Machine Learning & AI",
  "Cloud Computing & DevOps",
  "Web Development",
  "Mobile App Development",
  "Cybersecurity",
  "Product Management",
  "UI/UX Design",
  "Software Engineering & Architecture",
  "Blockchain",
  "Digital Marketing",
  "Finance & Analytics",
];

function FormView({ isUpdate = false }) {
  const dispatch = useDispatch();
  const { mentorInfo } = useSelector((state) => ({
    mentorInfo: state.mentors.mentorInfo,
  }));

  const [formInfo, setFormInfo] = useState(INITIAL_STATE);

  useEffect(() => {
    if (isUpdate && mentorInfo && mentorInfo._id) {
      setFormInfo({
        ...INITIAL_STATE,
        ...mentorInfo,
        displayName: mentorInfo.displayName || "",
        profileImage: mentorInfo.profileImage || "",
        domains: mentorInfo.domains || [],
        expertiseAreas: mentorInfo.expertiseAreas || [],
        credentials:
          mentorInfo.credentials && mentorInfo.credentials.length > 0
            ? mentorInfo.credentials.map((c) => ({
                type: c.type || "Certification",
                title: c.title || "",
                institution: c.institution || "",
                year: c.year || "",
              }))
            : [],
        yearsOfExperience: mentorInfo.yearsOfExperience || 0,
        status: mentorInfo.status || "DRAFT",
      });
    } else {
      setFormInfo(INITIAL_STATE);
    }
  }, [isUpdate, mentorInfo]);

  const FormSchema = Yup.object().shape({
    name: Yup.string().required("Mentor full name is required."),
    displayName: Yup.string().max(100, "Display name should be 100 characters max.").nullable(),
    email: Yup.string()
      .email("Invalid email address.")
      .required("Email is required for identification."),
    headline: Yup.string().max(200, "Headline should be 200 characters max."),
    yearsOfExperience: Yup.number()
      .min(0, "Years of experience cannot be negative.")
      .nullable(),
    status: Yup.string().oneOf(["DRAFT", "ACTIVE", "INACTIVE"]).required(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formInfo,
    validationSchema: FormSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          ...values,
          yearsOfExperience: Number(values.yearsOfExperience) || 0,
          credentials: (values.credentials || [])
            .filter((c) => c && (c.title || c.institution))
            .map((c) => ({
              ...c,
              year: c.year ? Number(c.year) : null,
            })),
        };

        if (isUpdate && mentorInfo?._id) {
          await dispatch(updateMentor(mentorInfo._id, payload));
        } else {
          await dispatch(createMentor(payload));
        }
        dispatch(getMentorList());
        dispatch(hideDialog());
      } catch (err) {
        console.error("Form submit error:", err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    isSubmitting,
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        className="mentor-form p-2"
      >
        {/* Top Section: Photo & Basic Details */}
        <div className="row g-3">
          {/* Profile Photo */}
          <div className="col-12 col-md-3 text-center border-end pe-3">
            <label className="form-label fw-bold d-block mb-2">
              Profile Photo
            </label>
            <div className="d-flex justify-content-center">
              <UploadImage
                fileList={
                  values.profileImage
                    ? [
                        {
                          uid: "-1",
                          name: "profile.png",
                          status: "done",
                          url: values.profileImage,
                        },
                      ]
                    : []
                }
                setFileList={() => {}}
                imageUrl={values.profileImage}
                setImageUrl={(url) => setFieldValue("profileImage", url)}
                aspect={1 / 1}
              />
            </div>
            <small className="text-muted d-block mt-2">
              Recommended: Square 1:1 ratio
            </small>

            <div className="mt-4 text-start">
              <label className="form-label fw-bold">
                Status <span className="text-danger">*</span>
              </label>
              <Select
                className="w-100"
                value={values.status}
                onChange={(val) => setFieldValue("status", val)}
              >
                <Option value="DRAFT">
                  <span className="badge bg-warning text-dark">Draft (Pending Review)</span>
                </Option>
                <Option value="ACTIVE">
                  <span className="badge bg-success">Active (Verified)</span>
                </Option>
                <Option value="INACTIVE">
                  <span className="badge bg-secondary">Inactive</span>
                </Option>
              </Select>
            </div>
          </div>

          {/* Core Info Fields */}
          <div className="col-12 col-md-9 ps-3">
            <div className="row g-2">
              <div className="col-md-6">
                <div className="form-group mb-2">
                  <label className="form-label fw-bold mb-1">
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      touched.name && errors.name ? "is-invalid" : ""
                    }`}
                    placeholder="e.g. Dr. Jane Elizabeth Doe"
                    {...getFieldProps("name")}
                  />
                  {touched.name && errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-2">
                  <label className="form-label fw-bold mb-1">
                    Display Name{" "}
                    <small className="text-muted fw-normal">
                      (Public / Card Name)
                    </small>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      touched.displayName && errors.displayName ? "is-invalid" : ""
                    }`}
                    placeholder="e.g. Dr. Jane Doe"
                    {...getFieldProps("displayName")}
                  />
                  {touched.displayName && errors.displayName && (
                    <div className="invalid-feedback">{errors.displayName}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-2">
                  <label className="form-label fw-bold mb-1">
                    Email Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className={`form-control ${
                      touched.email && errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="e.g. mentor@domain.com"
                    {...getFieldProps("email")}
                  />
                  {touched.email && errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-2">
                  <label className="form-label fw-bold mb-1">Phone Number (Optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. +1 555-0199"
                    {...getFieldProps("phone")}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-2">
                  <label className="form-label fw-bold mb-1">Current Designation</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Principal AI Scientist"
                    {...getFieldProps("designation")}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-2">
                  <label className="form-label fw-bold mb-1">Organization / Company</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Google / Microsoft"
                    {...getFieldProps("organization")}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-2">
                  <label className="form-label fw-bold mb-1">Years of Industry Experience</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    placeholder="e.g. 12"
                    {...getFieldProps("yearsOfExperience")}
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-2">
                  <label className="form-label fw-bold mb-1">
                    Headline / Tagline{" "}
                    <small className="text-muted fw-normal">
                      (Short callout displayed on embedded guidance cards)
                    </small>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Helping engineers transition into Data Science & MLOps"
                    maxLength={200}
                    {...getFieldProps("headline")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr className="my-3 text-muted" />

        {/* Section 2: Guidance Domains & Expertise Tags */}
        <div className="row g-2">
          <div className="col-md-6">
            <div className="form-group mb-2">
              <label className="form-label fw-bold mb-1">
                Domains{" "}
                <small className="text-muted fw-normal">
                  (Matched with Skill Pages & Blog Topics)
                </small>
              </label>
              <Select
                mode="tags"
                className="w-100"
                placeholder="Select or type domain names..."
                value={values.domains}
                onChange={(tags) => setFieldValue("domains", tags)}
                tokenSeparators={[","]}
              >
                {PREDEFINED_DOMAINS.map((domain) => (
                  <Option key={domain} value={domain}>
                    {domain}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group mb-2">
              <label className="form-label fw-bold mb-1">
                Expertise Tags{" "}
                <small className="text-muted fw-normal">
                  (Specific skills shown on the mentor badge)
                </small>
              </label>
              <Select
                mode="tags"
                className="w-100"
                placeholder="e.g. PyTorch, Kubernetes, System Design..."
                value={values.expertiseAreas}
                onChange={(tags) => setFieldValue("expertiseAreas", tags)}
                tokenSeparators={[","]}
              />
            </div>
          </div>

          <div className="col-12">
            <div className="form-group mb-2">
              <label className="form-label fw-bold mb-1">Detailed Bio</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Share full background, achievements, mentorship philosophy..."
                {...getFieldProps("bio")}
              />
            </div>
          </div>
        </div>

        <hr className="my-3 text-muted" />

        {/* Section 3: Credentials & Certifications (Dynamic Rows) */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label fw-bold mb-0">
              <i className="fa fa-graduation-cap me-1" /> Credentials & Certifications
            </label>
          </div>

          <FieldArray
            name="credentials"
            render={(arrayHelpers) => (
              <div>
                {values.credentials && values.credentials.length > 0 ? (
                  <div className="table-responsive border rounded mb-2">
                    <table className="table table-sm table-bordered mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "20%" }}>Type</th>
                          <th style={{ width: "35%" }}>Title / Degree / Award</th>
                          <th style={{ width: "30%" }}>Institution / Issuing Body</th>
                          <th style={{ width: "10%" }}>Year</th>
                          <th style={{ width: "5%" }} className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.credentials.map((cred, index) => (
                          <tr key={index}>
                            <td>
                              <Select
                                size="small"
                                className="w-100"
                                value={cred.type || "Certification"}
                                onChange={(val) =>
                                  setFieldValue(`credentials.${index}.type`, val)
                                }
                              >
                                {CREDENTIAL_TYPE_OPTIONS.map((opt) => (
                                  <Option key={opt} value={opt}>
                                    {opt}
                                  </Option>
                                ))}
                              </Select>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g. AWS Solutions Architect"
                                value={cred.title || ""}
                                onChange={(e) =>
                                  setFieldValue(`credentials.${index}.title`, e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g. Amazon Web Services"
                                value={cred.institution || ""}
                                onChange={(e) =>
                                  setFieldValue(`credentials.${index}.institution`, e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="YYYY"
                                value={cred.year || ""}
                                onChange={(e) =>
                                  setFieldValue(`credentials.${index}.year`, e.target.value)
                                }
                              />
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm p-1"
                                title="Remove Credential"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted small mb-2 fst-italic">
                    No credentials added yet. Click below to add degrees, certifications, or awards.
                  </p>
                )}

                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => arrayHelpers.push({ ...INITIAL_CREDENTIAL })}
                >
                  <i className="fa fa-plus me-1" /> Add Credential Row
                </button>
              </div>
            )}
          />
        </div>

        <hr className="my-3 text-muted" />

        {/* Section 4: Social / Links */}
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <div className="form-group mb-0">
              <label className="form-label fw-bold mb-1">
                <i className="fa fa-linkedin-square text-primary me-1" /> LinkedIn Profile URL
              </label>
              <input
                type="url"
                className="form-control"
                placeholder="https://linkedin.com/in/..."
                {...getFieldProps("linkedinUrl")}
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group mb-0">
              <label className="form-label fw-bold mb-1">
                <i className="fa fa-globe text-secondary me-1" /> Website / Portfolio URL
              </label>
              <input
                type="url"
                className="form-control"
                placeholder="https://..."
                {...getFieldProps("websiteUrl")}
              />
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
          <button
            type="button"
            className="btn btn-outline-secondary px-4"
            onClick={() => dispatch(hideDialog())}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary px-4"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Saving..."
              : isUpdate
              ? "Update Mentor Profile"
              : "Create Mentor Profile"}
          </button>
        </div>
      </Form>
    </FormikProvider>
  );
}

export default FormView;
